import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { BRAND } from "@/config/brand";
import { useSeo } from "@/hooks/useSeo";
import { Markdown } from "@/components/Markdown";
import { getArticle, formatArticleDate } from "@/content/articles";

export default function BlogPost() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const isDraft = article?.status === "draft";

  useSeo({
    title: article?.title,
    absoluteTitle: article?.seoTitle,
    description: article?.description,
    path: `/blog/${slug}`,
    type: "article",
    noindex: !article || isDraft,
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0908] bg-noise px-5 text-center" data-testid="blog-not-found">
        <h1 className="font-display text-3xl text-[#F4F1EA]">Article not found</h1>
        <Link to="/blog" className="mt-4 rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-[#0A0908]" data-testid="blog-not-found-back">
          Back to Journal
        </Link>
      </div>
    );
  }

  const jsonLd = !isDraft
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.description,
          datePublished: article.date,
          ...(article.heroImage ? { image: article.heroImage } : {}),
          author: { "@type": "Organization", name: BRAND.name },
          publisher: { "@type": "Organization", name: BRAND.name },
          mainEntityOfPage: `${BRAND.url}/blog/${article.slug}`,
        },
        ...(article.faq?.length
          ? [{
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: article.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }]
          : []),
      ]
    : null;

  return (
    <div className="min-h-screen bg-[#0A0908] bg-noise px-5 py-8" data-testid="blog-post">
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      <article className="mx-auto max-w-2xl">
        <Link to="/blog" className="mb-6 inline-flex items-center gap-1 text-sm text-[#8C8578] hover:text-[#D4AF37]" data-testid="blog-post-back">
          <ArrowLeft className="h-4 w-4" /> Journal
        </Link>

        <header>
          <div className="flex items-center gap-3 text-xs text-[#8C8578]">
            <time dateTime={article.date} data-testid="blog-post-date">{formatArticleDate(article.date)}</time>
            <span className="flex items-center gap-1" data-testid="blog-post-readtime">
              <Clock className="h-3 w-3" /> {article.readMinutes} min read
            </span>
            <span className="text-[#8C8578]">by {BRAND.name}</span>
          </div>
          <h1 className="mt-3 font-display text-4xl font-medium tracking-tight text-[#F4F1EA]" data-testid="blog-post-title">
            {article.title}
          </h1>
        </header>

        {isDraft && (
          <div className="mt-6 rounded-2xl border border-[#5c4a1e] bg-[#1a1509] p-5" data-testid="blog-draft-notice">
            <p className="text-sm text-[#F4F1EA]">This article is still being written.</p>
            <p className="mt-1 text-xs text-[#C8A24A]">Check back soon — or come say hi in the app meanwhile.</p>
          </div>
        )}

        {article.heroImage && (
          <img
            src={article.heroImage}
            alt={article.heroAlt || ""}
            className="mt-8 w-full rounded-2xl border border-[#2B241B] object-cover"
            data-testid="blog-post-hero"
          />
        )}

        <div className="mt-8" data-testid="blog-post-body">
          <Markdown>{article.body}</Markdown>
        </div>

        <footer className="mt-12 border-t border-[#2B241B] pt-6">
          <Link to="/blog" className="text-sm text-[#D4AF37] hover:text-[#E5C158]" data-testid="blog-post-footer-back">
            ← More from the Journal
          </Link>
        </footer>
      </article>
    </div>
  );
}
